import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { RelatedChannels } from "@/components/calv/RelatedChannels";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/leistungen/performance-marketing/microsoft-ads")({
  head: () => {
    const h = pageHead("pmMicrosoftAds", "de");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "Microsoft Ads Agentur",
          description: "Bing-Search, LinkedIn-Profil-Targeting, Audience Network — als Search-Komplement gesteuert, nicht als Google-Klon.",
          url: "/leistungen/performance-marketing/microsoft-ads",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Start", path: "/" },
          { name: "Leistungen", path: "/leistungen" },
          { name: "Performance Marketing", path: "/leistungen/performance-marketing" },
          { name: "Microsoft Ads", path: "/leistungen/performance-marketing/microsoft-ads" },
        ])),
      ],
    };
  },
  component: MicrosoftAdsDE,
});

const steps = [
  { num: "01", title: "Analyse & Google-Mix-Check (3–5 Tage)", body: "Account-Audit (falls schon Microsoft-Aktivität läuft), Analyse des bestehenden Google-Setups als Komplementär-Basis, Audience-Affinitäts-Check, Wettbewerbs-Analyse im Bing-Suchergebnis. Output: konkrete Hypothesen plus Wirtschaftlichkeits-Einordnung." },
  { num: "02", title: "Setup mit Microsoft-Spezifika (1–2 Wochen)", body: "UET-Tag plus Conversions-API-Integration. Account-Struktur Microsoft-spezifisch — nicht aus Google-Import, sondern frisch gedacht. LinkedIn-Profil-Targeting wo passend. Audience Network selektiv. Erste Bid-Strategien-Adjustments." },
  { num: "03", title: "Iteration & Search-Mix-Optimierung (ab Woche 2–3)", body: "Iteration auf Audience-Schärfe, Match-Type-Disziplin, Negative-Listen-Pflege. Parallele Steuerung mit Google Ads als komplementärer Layer — Budgets dynamisch zwischen Google und Microsoft, je nachdem, wo die wirtschaftliche Performance besser ist." },
  { num: "04", title: "Skalierung & laufender Betrieb (kontinuierlich)", body: "Budget-Skalierung in funktionierenden Bereichen. LinkedIn-Profil-Targeting-Ausbau im B2B-Kontext. Monatlicher Performance-Review im integrierten Search-Kontext. Operative Steuerung in der Regel direkt durch den Geschäftsführer." },
];

const capabilities = [
  { name: "Bing-Search-Kampagnen", detail: "Brand · Non-Brand · Long-Tail" },
  { name: "LinkedIn-Profil-Targeting", detail: "Job Function · Company · Industry" },
  { name: "Microsoft Shopping", detail: "Feed-Hygiene über Delta" },
  { name: "Audience Network selektiv", detail: "MSN · Outlook · Edge" },
  { name: "UET-Tag & Conversions API", detail: "Server-side · Match-Quality" },
  { name: "Smart Bidding", detail: "Lernphasen-Disziplin" },
  { name: "Negativ-Listen-Hygiene", detail: "Regelbasiert · automatisiert" },
  { name: "Search-Mix-Steuerung", detail: "Google + Microsoft integriert" },
];

const audience = [
  { tag: "Primär", title: "B2B Lead-Generation", body: "Microsoft Ads funktioniert besonders stark im B2B, weil die Bing-Audience eine höhere Affinität zur beruflichen Nutzung hat. LinkedIn-Profil-Targeting im Search-Kontext ist hier der größte Hebel — verfügbar nur bei Microsoft, nicht bei Google." },
  { tag: "Sekundär", title: "B2C E-Commerce als Search-Komplement", body: "Microsoft Ads als Ergänzung zu Google Search lohnt sich für E-Commerce-Setups ab Monatsbudgets von etwa €5.000 in Google. Niedrigere CPCs plus kaufkräftigere Audience führen oft zu besseren ROAS-Werten." },
  { tag: "Tertiär", title: "SaaS mit B2B-Schwerpunkt", body: "Funktioniert für SaaS-Setups, wo die Buyer-Audience im Microsoft-Ökosystem stark vertreten ist (Office, Azure, LinkedIn). Bottom-Funnel-Search plus LinkedIn-Profil-Targeting als Account-Based-Layer." },
  { tag: "Disqualifikation", title: "Wann wir nicht der richtige Partner sind", body: "Bei isolierten Microsoft-Ads-Mandaten unter €1.500 Monatsbudget. Wenn die Erwartung ist “verdoppeln Sie meinen Search-Traffic” — Microsoft Ads ist kein Volumen-Kanal. Wenn Google Ads noch nicht sauber aufgesetzt ist." },
];

const realistic = [
  "CPC-Vorteil von 30–50 % gegenüber Google ist im DACH-Markt typisch — niedrigere Auktions-Drücke bei gleicher oder ähnlicher Audience-Qualität.",
  "ROAS-Vorteil von 15–30 % gegenüber Google bei B2B-Setups ist eine typische Bandbreite, wenn LinkedIn-Profil-Targeting systematisch genutzt wird.",
  "Search-Mix-Diversifikation um 10–20 % Volumen-Hebel — Microsoft Ads ergänzt Google statt zu ersetzen. Der Volumen-Beitrag ist relativ klein, aber zusätzlich.",
  "Conversion-Stabilität in B2B-Audiences höher als bei Google — schwer in Prozent zu beziffern, aber in den meisten B2B-Setups beobachtbar.",
];

const value = [
  ["Search-Mix-Diversifikation statt Google-Monokultur.", "Risiko-Streuung über zwei Such-Kanäle, nicht Abhängigkeit von einem."],
  ["LinkedIn-Profil-Targeting im Search-Kontext.", "Exklusiver Hebel für B2B, der bei Google nicht verfügbar ist."],
  ["Niedrigere CPCs bei vergleichbarer Conversion-Qualität.", "Effizienz-Hebel im gesamten Search-Setup."],
  ["Microsoft-eigene Account-Logik statt Google-Import.", "Wir bauen kanal-spezifisch, nicht copy-paste."],
  ["Operative Steuerung durch erfahrene Hand.", "Direkt durch den Geschäftsführer in den meisten Mandaten."],
  ["Vergütungs-Modell ohne Budget-Kopplung.", "Wir verdienen nicht mehr, wenn Sie mehr Budget einsetzen."],
];

const related = [
  { to: "/leistungen/performance-marketing/google-ads", name: "Google Ads", sub: "Komplementärer Search-Kanal mit höherem Volumen-Anteil" },
  { to: "/leistungen/performance-marketing/linkedin-ads", name: "LinkedIn Ads", sub: "B2B-Spezialisierung mit eigener Audience-Logik" },
  { to: "/leistungen/tracking-datenbasis", name: "Tracking & Datenbasis", sub: "Saubere Daten als Voraussetzung für Search-Mix-Steuerung" },
];

const channelLinks = [
  { to: "/leistungen/performance-marketing/google-ads", name: "Google Ads", sub: "Search, Shopping, PMax und Demand Gen — Performance auf Googles Eigentums-Fläche." },
  { to: "/leistungen/performance-marketing/linkedin-ads", name: "LinkedIn Ads", sub: "B2B-Targeting nach Funktion, Branche und Unternehmensgröße." },
];

function MicrosoftAdsDE() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · Microsoft Ads"
          headline={<>Microsoft Ads Agentur — <em>Search-Komplement für DACH,</em> nicht Google-Klon.</>}
          lead="Bing-Search, LinkedIn-Profil-Targeting, Audience Network — als Ergänzung im Such-Mix gesteuert, nicht als isolierte Insel. Calvarius arbeitet als Microsoft Ads Agentur für B2B-Setups, SaaS-Anbieter und B2C-E-Commerce, die ihren Search-Mix wirtschaftlich diversifizieren wollen."
          metaStrip={["B2B-Affinität", "Niedrige CPCs", "LinkedIn-Profil-Targeting"]}
          breadcrumbs={[
            { label: "Start", to: "/" },
            { label: "Leistungen", to: "/leistungen" },
            { label: "Performance Marketing", to: "/leistungen/performance-marketing" },
            { label: "Microsoft Ads" },
          ]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Was eine Microsoft Ads Agentur im DACH-Markt <em>wirklich leistet.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Wer eine Microsoft Ads Agentur für den deutschsprachigen Markt sucht, hat in der Regel eine konkrete Frage: Lohnt sich der Aufwand für einen Kanal mit nur etwa 7-10 % Marktanteil im DACH-Such-Markt? Die ehrliche Antwort: in vielen Setups ja, in einigen nein. Microsoft Ads ist kein Volumen-Kanal wie Google — es ist ein <strong className="font-semibold text-ink">Effizienz- und Diversifikations-Kanal</strong>. Wer Search-Risiko streuen will, wer eine kaufkräftigere Audience adressieren will, wer LinkedIn-Profil-Targeting im Search-Kontext nutzen will, findet in Microsoft Ads echten wirtschaftlichen Hebel — wenn der Setup wirtschaftlich groß genug ist, dass die operative Pflege sich lohnt.</p>
              <p className="mb-5">Calvarius behandelt Microsoft Ads nicht als Google-Kopie. Das wäre der häufigste Fehler — Google-Ads-Kampagnen 1:1 in Microsoft zu importieren und zu hoffen, dass es funktioniert. Tatsächlich hat Microsoft Ads eigene Stärken: deutlich niedrigere CPCs (oft 30-50 % unter Google), höhere Conversion-Stabilität in B2B-Audiences, einzigartige LinkedIn-Profil-Targeting-Optionen (Job Function, Company, Industry) direkt im Search-Kontext. Diese Stärken nutzen wir gezielt — statt das Google-Setup nochmal nachzubauen.</p>
              <p>Konkret heißt das: Wir bauen Microsoft-spezifische Account-Strukturen. UET-Tag plus Server-side Tracking für saubere Attribution. Audience Network selektiv dort, wo es wirtschaftlich tragbar ist. Und wir verbinden Microsoft Ads mit der bestehenden <Link to="/leistungen/performance-marketing/google-ads" className="underline">Google-Ads-Strategie</Link> als komplementärer Layer, nicht als Konkurrenz-Konstrukt.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Zielgruppen-Einordnung" headline={<>Wann Sie eine Microsoft Ads Agentur <em>brauchen — und wann nicht.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodik" headline={<>Wie unsere Microsoft Ads Agentur <em>Mandate startet.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-10 max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7">
              <p className="eyebrow mb-3">Schneller Start als Wettbewerbs-Vorteil</p>
              <p className="text-[15px] leading-relaxed text-ink-2">Während klassische Performance-Agenturen Microsoft Ads oft als Google-Import-Job behandeln und drei bis sechs Wochen für ein Setup ohne echte Microsoft-Spezifika brauchen, sind wir innerhalb von zwei Wochen mit einem kanal-eigenen Setup produktiv.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="Was wir liefern" headline={<>Was unsere Microsoft Ads Agentur <em>konkret liefert.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Tooling" headline={<>Tools und Stack unserer <em>Microsoft-Ads-Steuerung.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Native Plattform</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Microsoft Advertising UI</li>
                  <li>Advertising Editor (Bulk)</li>
                  <li>UET-Tag + Conversions API</li>
                  <li>Microsoft Merchant Center</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Tracking & Analytics</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Server-side via GTM-Server</li>
                  <li>Google Analytics 4</li>
                  <li>Looker Studio Search-Mix-Dashboards</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Calvarius-Tools</p>
                <ul className="space-y-3 text-sm text-ink-2">
                  <li><Link to="/leistungen/delta" className="font-medium text-ink hover:text-cyan-deep">Delta →</Link><br/><span className="text-ink-3">Feed-Management auch für Microsoft Shopping</span></li>
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="Was realistisch ist" headline={<>Realistische Ergebnisse einer <em>Microsoft-Ads-Optimierung.</em></>}
          caption="Microsoft Ads ist kein Skalierungs-Kanal, sondern ein Diversifikations- und Effizienz-Kanal.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i+1).padStart(2,"0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="Was Sie gewinnen" headline={<>Was Sie von unserer <em>Microsoft Ads Agentur</em> bekommen.</>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedChannels locale="de" items={channelLinks} />

        <Section variant="tinted" number="09" kicker="Weiter vertiefen" headline={<>Verwandte Disziplinen.</>}
          caption="Diese Themen wirken im Zusammenspiel.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/leistungen/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← Alle Performance-Marketing-Disziplinen
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Konkret werden">
          <CTABlock
            headline={<>Microsoft Ads Audit in 30 Minuten — <em>kostenlos und unverbindlich.</em></>}
            body="In einem ersten Gespräch klären wir, ob Microsoft Ads in Ihrem Setup wirtschaftlich Sinn macht — als Komplement zu Google oder als B2B-Spezial-Kanal. Sie bekommen eine ehrliche Einordnung."
            primary={{ label: "Gespräch vereinbaren", to: "/kontakt" }}
            secondary={{ label: "Performance-Marketing-Übersicht", to: "/leistungen/performance-marketing" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
